import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Check,
  ChevronDown,
  ChevronRight,
  Star,
  Kanban,
  Briefcase,
  UserCheck,
  CreditCard,
  Sparkles,
  ArrowRight,
  Shield,
  Zap,
  BarChart3,
  Clock,
  FileText,
  Users,
  CheckCircle2,
  Layers,
  Building2,
  Menu,
  X,
} from 'lucide-react'

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'collab' | 'sales' | 'hrms' | 'billing' | 'cockpit'>('collab')
  const [activeModuleIndex, setActiveModuleIndex] = useState(0)
  const [faqOpen, setFaqOpen] = useState<number | null>(null)

  const modules = [
    {
      id: 'projects',
      title: 'Projects & Spaces',
      badge: 'Agile & Kanban',
      stat: 'Sprint Velocity 91%',
      description: 'Dynamic sprint boards, waterfall Gantt charts, task backlogs, and milestone tracking designed for cross-functional teams.',
      image: '/snapshots/projects_spaces.png',
      highlights: ['Interactive Kanban boards', 'Milestone & Gantt delivery schedules', 'Custom task fields & checklists'],
    },
    {
      id: 'sales',
      title: 'Sales & CRM',
      badge: 'Revenue Pipeline',
      stat: '₹3.5M Deals Closed',
      description: 'End-to-end quotation workflows, proforma invoices, client relationship management, and deal stage automation.',
      image: '/snapshots/sales_crm.png',
      highlights: ['Multi-tier quotation approval', 'Instant GST-compliant tax invoices', 'Downloadable PDF invoices & receipts'],
    },
    {
      id: 'hrms',
      title: 'People & Workforce',
      badge: 'HRMS Suite',
      stat: '480+ Personnel',
      description: 'Biometric punch sync, real-time attendance logs, leave request approvals, and departmental organization directory.',
      image: '/snapshots/people_hrms.png',
      highlights: ['Live punch-in / punch-out tracking', 'Leave balance policies & approvals', 'Role-based access & profile permissions'],
    },
    {
      id: 'timeline',
      title: 'Portfolio Timeline',
      badge: 'Delivery Roadmaps',
      stat: '100% On-Track',
      description: 'Unified cross-project timelines with dependency visualization, team capacity planning, and burndown analytics.',
      image: '/snapshots/projects_timeline.png',
      highlights: ['Cross-project roadmap view', 'Resource workload distribution', 'Real-time progress sync across spaces'],
    },
    {
      id: 'dashboard',
      title: 'Workplace Cockpit',
      badge: 'Executive Hub',
      stat: '99.98% Uptime',
      description: 'Single-pane-of-glass executive dashboard summarizing live attendance, sales revenue, sprint progress, and pending approvals.',
      image: '/snapshots/dashboard_overview.png',
      highlights: ['Unified KPI metrics & alerts', 'Actionable approval queues', 'Real-time department scoreboards'],
    },
  ]

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveModuleIndex((prev) => (prev + 1) % modules.length)
    }, 6000)
    return () => clearInterval(timer)
  }, [modules.length])

  const faqs = [
    {
      q: 'Does Zynx Workplace support Indian GST and tax invoice generation?',
      a: 'Yes, absolutely. Zynx includes comprehensive Indian GST support with state tax matrices, HSN/SAC codes, CGST/SGST/IGST calculation, proforma invoices, and one-click PDF tax invoice and receipt downloads.',
    },
    {
      q: 'Can our organization connect biometric attendance machines?',
      a: 'Yes. Zynx features built-in biometric device listener endpoints (/iclock protocol) and direct device IP sync for automated punch capture, shifts, and daily attendance logs.',
    },
    {
      q: 'Can we deploy Zynx Workplace on our private cloud or VPS?',
      a: 'Yes. The Enterprise tier supports isolated Dockerized on-premise deployments with dedicated MongoDB, automated backups, and complete data ownership.',
    },
    {
      q: 'Is there a free tier for startups and growing teams?',
      a: 'Yes! The Basic plan is completely free forever for teams up to 15 members, including full access to projects, Kanban boards, and CRM contacts.',
    },
  ]

  return (
    <div className="min-h-screen bg-[#0E0617] text-white font-sans selection:bg-[#8F529B] selection:text-white antialiased">
      {/* ===================== HERO SECTION ===================== */}
      <div className="relative overflow-hidden bg-gradient-to-b from-[#19062A] via-[#2A0E45] to-[#0E0617] border-b border-purple-900/30">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] pointer-events-none opacity-25 bg-[radial-gradient(ellipse_at_center,#A855F7_0%,transparent_70%)] blur-3xl" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] pointer-events-none opacity-20 bg-[radial-gradient(circle,#9333EA_0%,transparent_70%)] blur-3xl" />

        {/* TOP NAVBAR */}
        <header className="relative z-40 border-b border-white/10 backdrop-blur-md bg-[#19062A]/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-18 flex items-center justify-between">
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center gap-2.5 group">
                <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-[#9333EA] to-[#C084FC] flex items-center justify-center font-black text-white text-lg shadow-md shadow-purple-900/50 group-hover:scale-105 transition-transform">
                  Z
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-black tracking-tight text-white font-jakarta">zynx</span>
                    <span className="text-[10px] font-bold tracking-wider text-purple-300 uppercase px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/20">
                      Workplace
                    </span>
                  </div>
                </div>
              </Link>

              <nav className="hidden lg:flex items-center gap-1 text-sm font-semibold text-white/80">
                <div
                  className="relative"
                  onMouseEnter={() => setActiveDropdown('products')}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg hover:text-white hover:bg-white/5 transition-colors">
                    Products <ChevronDown className="h-3.5 w-3.5 text-white/60" />
                  </button>

                  {activeDropdown === 'products' && (
                    <div className="absolute top-full left-0 w-[540px] p-4 bg-[#1C0D2E] text-white border border-purple-500/20 rounded-2xl shadow-2xl shadow-black/80 grid grid-cols-2 gap-3 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                      <Link to="/register" className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-white/5 transition-colors">
                        <div className="p-2 rounded-lg bg-blue-500/15 text-blue-400">
                          <Kanban className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-white">Agile Projects</p>
                          <p className="text-[11px] text-white/60">Kanban, Sprints & Gantt schedules.</p>
                        </div>
                      </Link>
                      <Link to="/register" className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-white/5 transition-colors">
                        <div className="p-2 rounded-lg bg-emerald-500/15 text-emerald-400">
                          <Briefcase className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-white">Sales & CRM</p>
                          <p className="text-[11px] text-white/60">Quotations, deals & tax invoices.</p>
                        </div>
                      </Link>
                      <Link to="/register" className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-white/5 transition-colors">
                        <div className="p-2 rounded-lg bg-purple-500/15 text-purple-400">
                          <UserCheck className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-white">HRMS Suite</p>
                          <p className="text-[11px] text-white/60">Biometric punches, leaves & shifts.</p>
                        </div>
                      </Link>
                      <Link to="/register" className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-white/5 transition-colors">
                        <div className="p-2 rounded-lg bg-teal-500/15 text-teal-400">
                          <CreditCard className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-white">GST Invoicing</p>
                          <p className="text-[11px] text-white/60">Running bills & receipt generation.</p>
                        </div>
                      </Link>
                    </div>
                  )}
                </div>

                <a href="#solutions" className="px-3.5 py-2 rounded-lg hover:text-white hover:bg-white/5 transition-colors">
                  Solutions
                </a>
                <a href="#pricing" className="px-3.5 py-2 rounded-lg hover:text-white hover:bg-white/5 transition-colors">
                  Pricing
                </a>
                <a href="#faqs" className="px-3.5 py-2 rounded-lg hover:text-white hover:bg-white/5 transition-colors">
                  FAQ
                </a>
              </nav>
            </div>

            <div className="hidden sm:flex items-center gap-3">
              <Link
                to="/login"
                className="text-sm font-semibold text-white/90 hover:text-white px-4 py-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                Sign In
              </Link>
              <a
                href="https://durranitech.com/contact"
                target="_blank"
                rel="noreferrer"
                className="hidden md:inline-flex text-sm font-semibold text-white/80 hover:text-white px-3 py-2"
              >
                Contact Sales
              </a>
              <Link
                to="/register"
                className="h-10 px-5 rounded-full bg-gradient-to-r from-[#9333EA] to-[#C084FC] hover:from-[#8228d4] hover:to-[#b370f5] text-white text-xs font-bold font-jakarta transition-all shadow-md shadow-purple-900/40 flex items-center justify-center gap-1.5"
              >
                Start Free Trial <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/5"
              aria-label="Toggle Menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="lg:hidden px-4 pt-3 pb-6 border-t border-white/10 bg-[#19062A] space-y-3">
              <Link to="/register" className="block text-sm font-semibold text-white/90 py-2">
                Agile Projects & Kanban
              </Link>
              <Link to="/register" className="block text-sm font-semibold text-white/90 py-2">
                Sales CRM & Quotations
              </Link>
              <Link to="/register" className="block text-sm font-semibold text-white/90 py-2">
                Workforce & HRMS
              </Link>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-semibold text-white/90 py-2">
                Pricing
              </a>
              <div className="pt-3 border-t border-white/10 flex flex-col gap-2">
                <Link to="/login" className="text-center py-2.5 rounded-lg border border-white/20 text-sm font-semibold text-white">
                  Sign In
                </Link>
                <Link to="/register" className="text-center py-2.5 rounded-lg bg-purple-600 text-sm font-semibold text-white">
                  Start Free Trial
                </Link>
              </div>
            </div>
          )}
        </header>

        {/* HERO BODY */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-16 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-400/25 text-purple-200 text-xs font-semibold mb-6 backdrop-blur-sm">
            <Sparkles className="h-3.5 w-3.5 text-purple-400" />
            <span>Zynx Workplace 2.0 with GST Invoicing & Biometric HRMS is Live</span>
          </div>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight font-jakarta text-white max-w-5xl mx-auto leading-[1.08] mb-6">
            Where modern teams organize,{' '}
            <span className="bg-gradient-to-r from-[#D8B4FE] via-[#F472B6] to-[#C084FC] bg-clip-text text-transparent">
              collaborate, and scale.
            </span>
          </h1>

          <p className="text-base sm:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed mb-8 font-normal">
            The unified AI-ready business OS bridging projects, sales pipeline, quotation approvals, workforce attendance, and India GST billing in one cohesive platform.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mb-14">
            <Link to="/register">
              <button
                type="button"
                className="h-12 px-8 rounded-full bg-gradient-to-r from-[#9333EA] to-[#A855F7] hover:from-[#8228d4] hover:to-[#9333EA] text-white text-sm font-bold font-jakarta transition-all shadow-xl shadow-purple-950/60 flex items-center justify-center gap-2 group"
              >
                Start 14-Day Free Trial
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </Link>
            <a href="#showcase">
              <button
                type="button"
                className="h-12 px-7 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-semibold font-jakarta border border-white/20 transition-all flex items-center justify-center gap-2"
              >
                View Product Showcase
              </button>
            </a>
          </div>

          {/* INTERACTIVE PRODUCT DECK */}
          <div id="showcase" className="relative max-w-6xl mx-auto scroll-mt-24">
            <div className="flex items-center justify-start sm:justify-center gap-2 pb-4 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap mb-4">
              {modules.map((mod, idx) => (
                <button
                  key={mod.id}
                  onClick={() => setActiveModuleIndex(idx)}
                  className={`px-4 py-2.5 rounded-full text-xs sm:text-sm font-bold font-jakarta transition-all whitespace-nowrap flex items-center gap-2 ${
                    activeModuleIndex === idx
                      ? 'bg-purple-600 text-white shadow-lg shadow-purple-900/50 border border-purple-400/40'
                      : 'bg-white/5 hover:bg-white/10 text-white/70 border border-white/10'
                  }`}
                >
                  <span>{mod.title}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${activeModuleIndex === idx ? 'bg-white/20 text-white' : 'bg-white/10 text-white/50'}`}>
                    {mod.badge}
                  </span>
                </button>
              ))}
            </div>

            <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-[#1C0D2E] to-[#120720] border border-purple-500/25 p-4 sm:p-7 shadow-2xl shadow-black/90 text-left">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                <div className="lg:col-span-5 space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/15 border border-purple-400/20 text-purple-300 text-xs font-bold">
                    <Sparkles className="h-3.5 w-3.5 text-purple-400" />
                    <span>{modules[activeModuleIndex].stat}</span>
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-extrabold text-white font-jakarta">
                    {modules[activeModuleIndex].title}
                  </h3>

                  <p className="text-sm text-white/70 leading-relaxed">
                    {modules[activeModuleIndex].description}
                  </p>

                  <div className="pt-2 space-y-2">
                    {modules[activeModuleIndex].highlights.map((h, i) => (
                      <div key={i} className="flex items-center gap-2.5 text-xs text-white/85">
                        <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                        <span>{h}</span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4">
                    <Link
                      to="/register"
                      className="inline-flex items-center gap-2 text-xs font-bold text-purple-300 hover:text-white transition-colors"
                    >
                      Explore {modules[activeModuleIndex].title} in live app <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>

                <div className="lg:col-span-7">
                  <div className="relative rounded-2xl overflow-hidden border border-white/15 bg-slate-950 shadow-xl group">
                    <img
                      src={modules[activeModuleIndex].image}
                      alt={modules[activeModuleIndex].title}
                      className="w-full h-auto max-h-[420px] object-cover object-top transition-transform duration-500 group-hover:scale-[1.01]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex items-end p-4">
                      <div className="flex items-center justify-between w-full text-xs text-white">
                        <span className="font-semibold text-white/90">Live Workplace Module View</span>
                        <span className="text-[11px] font-bold text-emerald-400 bg-emerald-950/80 border border-emerald-500/30 px-2 py-0.5 rounded-full">
                          Connected & Active
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===================== LOGO / TRUST CLOUD ===================== */}
      <section className="py-12 bg-[#120720] border-b border-purple-900/20 text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest text-purple-300/60 mb-6 font-jakarta">
            Engineered for high-performing operations and trusted across client ecosystems
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 text-white/60 font-jakarta font-bold text-sm sm:text-base">
            <span className="px-4 py-2 rounded-xl bg-white/5 border border-white/5 hover:border-purple-400/30 hover:text-white transition-colors">
              DURRANI TECH
            </span>
            <span className="px-4 py-2 rounded-xl bg-white/5 border border-white/5 hover:border-purple-400/30 hover:text-white transition-colors">
              ADVERTERS MEDIA
            </span>
            <span className="px-4 py-2 rounded-xl bg-white/5 border border-white/5 hover:border-purple-400/30 hover:text-white transition-colors">
              KSHETRA
            </span>
            <span className="px-4 py-2 rounded-xl bg-white/5 border border-white/5 hover:border-purple-400/30 hover:text-white transition-colors">
              GHAR KA PYAAR
            </span>
            <span className="px-4 py-2 rounded-xl bg-white/5 border border-white/5 hover:border-purple-400/30 hover:text-white transition-colors">
              TOOLVANTA
            </span>
            <span className="px-4 py-2 rounded-xl bg-white/5 border border-white/5 hover:border-purple-400/30 hover:text-white transition-colors">
              APEXAPPS
            </span>
          </div>
        </div>
      </section>

      {/* ===================== ONE PLATFORM WORKSPACE TABS ===================== */}
      <section id="solutions" className="py-20 sm:py-24 bg-[#0E0617] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-bold uppercase tracking-wider mb-3">
              <Layers className="h-3.5 w-3.5 text-purple-400" />
              Unified Ecosystem
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold font-jakarta tracking-tight text-white leading-tight">
              One platform.{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Endless ways to collaborate.
              </span>
            </h2>
            <p className="text-sm sm:text-base text-white/60 mt-3 max-w-xl mx-auto">
              Replace fragmented tools with an integrated business operating system built for modern productivity.
            </p>
          </div>

          <div className="flex items-center justify-start sm:justify-center gap-2 border-b border-white/10 pb-4 mb-12 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap">
            {[
              { id: 'collab', label: 'Collaboration & Tasks', icon: Kanban },
              { id: 'sales', label: 'Sales CRM & Deals', icon: Briefcase },
              { id: 'hrms', label: 'HRMS & Attendance', icon: UserCheck },
              { id: 'billing', label: 'GST & Invoicing', icon: CreditCard },
              { id: 'cockpit', label: 'Executive Cockpit', icon: BarChart3 },
            ].map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold font-jakarta transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-purple-600/30 text-purple-200 border border-purple-500/40 shadow-sm'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-[#150A22] rounded-3xl p-6 sm:p-10 border border-purple-900/30">
            <div className="lg:col-span-5 space-y-6">
              <div className="space-y-4">
                {activeTab === 'collab' && (
                  <>
                    <h3 className="text-xl font-bold font-jakarta text-white">Sprint-ready Agile Project Management</h3>
                    <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
                      Coordinate team tasks, sprints, and delivery milestones. Eliminate friction with full visibility into dependencies and checklists.
                    </p>
                    <ul className="space-y-2.5 text-xs text-white/80">
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-purple-400" /> Interactive Kanban boards with drag-and-drop</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-purple-400" /> Sprint backlog planning and burndown charts</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-purple-400" /> Customizable project workflows and priority gates</li>
                    </ul>
                  </>
                )}
                {activeTab === 'sales' && (
                  <>
                    <h3 className="text-xl font-bold font-jakarta text-white">High-Velocity Quotations & Sales Pipeline</h3>
                    <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
                      Accelerate deal cycles with structured quotation approvals, PDF invoice downloads, and integrated revenue milestones.
                    </p>
                    <ul className="space-y-2.5 text-xs text-white/80">
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" /> Multi-tier quotation approval matrices</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" /> Automated proforma invoices and receipt layout</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" /> Lead tracking and sales activity logs</li>
                    </ul>
                  </>
                )}
                {activeTab === 'hrms' && (
                  <>
                    <h3 className="text-xl font-bold font-jakarta text-white">Automated Workforce, Shifts & Leaves</h3>
                    <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
                      Empower human resources with automated biometric clock-ins, leave balance policies, and organization hierarchy.
                    </p>
                    <ul className="space-y-2.5 text-xs text-white/80">
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-blue-400" /> Live attendance and punch log syncing</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-blue-400" /> Employee code auto-generation and profiles</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-blue-400" /> Departmental shift schedules and holidays</li>
                    </ul>
                  </>
                )}
                {activeTab === 'billing' && (
                  <>
                    <h3 className="text-xl font-bold font-jakarta text-white">Complete India GST & Milestone Billing</h3>
                    <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
                      Stay 100% compliant with GST master matrices, HSN/SAC code registries, and instant PDF sales document generation.
                    </p>
                    <ul className="space-y-2.5 text-xs text-white/80">
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-400" /> Milestone-based running bill schedules</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-400" /> Professional letterhead PDF invoice exports</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-400" /> CGST, SGST & IGST breakdown calculation</li>
                    </ul>
                  </>
                )}
                {activeTab === 'cockpit' && (
                  <>
                    <h3 className="text-xl font-bold font-jakarta text-white">Executive Operations Cockpit</h3>
                    <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
                      Real-time visibility across company departments, pending approvals, team attendance ratios, and billed revenue.
                    </p>
                    <ul className="space-y-2.5 text-xs text-white/80">
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-pink-400" /> Live executive widgets and KPI counters</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-pink-400" /> High-priority approval alerts for managers</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-pink-400" /> Cross-organization activity streams</li>
                    </ul>
                  </>
                )}
              </div>

              <div className="pt-2">
                <Link to="/register">
                  <button
                    type="button"
                    className="h-11 px-6 rounded-full bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold font-jakarta transition-colors shadow-md shadow-purple-900/40 flex items-center gap-2"
                  >
                    Get Started with Zynx <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl overflow-hidden border border-purple-500/30 bg-slate-950 shadow-2xl">
                <div className="px-4 py-2.5 bg-[#1C0D2E] border-b border-purple-500/20 flex items-center justify-between text-[11px] text-white/70">
                  <div className="flex items-center gap-2">
                    <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
                    <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                    <div className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                    <span className="font-semibold text-white/80 ml-2">Zynx Enterprise Module</span>
                  </div>
                  <span className="text-emerald-400 font-bold bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-500/20">
                    Live Status: Active
                  </span>
                </div>
                <img
                  src={
                    activeTab === 'collab'
                      ? '/snapshots/projects_spaces.png'
                      : activeTab === 'sales'
                      ? '/snapshots/sales_crm.png'
                      : activeTab === 'hrms'
                      ? '/snapshots/people_hrms.png'
                      : activeTab === 'billing'
                      ? '/snapshots/settings_masterdata.png'
                      : '/snapshots/dashboard_overview.png'
                  }
                  alt="Zynx Workplace Platform"
                  className="w-full h-auto max-h-[380px] object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== METRICS / PROOF ===================== */}
      <section className="py-16 bg-[#120720] border-y border-purple-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="p-6 rounded-2xl bg-white/5 border border-white/5 space-y-1">
              <div className="text-3xl sm:text-4xl font-extrabold text-white font-jakarta">99.98%</div>
              <p className="text-xs text-white/60">Platform SLA Uptime</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/5 space-y-1">
              <div className="text-3xl sm:text-4xl font-extrabold text-purple-300 font-jakarta">480+</div>
              <p className="text-xs text-white/60">Active Personnel Managed</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/5 space-y-1">
              <div className="text-3xl sm:text-4xl font-extrabold text-pink-300 font-jakarta">₹3.5M+</div>
              <p className="text-xs text-white/60">Pipeline Deals Processed</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/5 space-y-1">
              <div className="text-3xl sm:text-4xl font-extrabold text-emerald-400 font-jakarta">100%</div>
              <p className="text-xs text-white/60">India GST & Audit Compliance</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== PRICING SECTION ===================== */}
      <section id="pricing" className="py-20 sm:py-24 bg-[#0E0617] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-bold uppercase tracking-wider mb-3">
              <CreditCard className="h-3.5 w-3.5 text-purple-400" />
              Simple Plans
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold font-jakarta tracking-tight text-white leading-tight">
              Simple, transparent pricing
            </h2>
            <p className="text-sm text-white/60 mt-2">
              Free forever for startups and small teams. Scale seamlessly as your organization expands.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto items-stretch">
            {/* Basic Plan */}
            <div className="p-8 rounded-3xl bg-[#150A22] border border-white/10 flex flex-col justify-between shadow-xl">
              <div>
                <h3 className="text-lg font-bold text-white font-jakarta">Basic</h3>
                <p className="text-xs text-white/60 mt-1">For startups & small teams</p>
                <div className="text-3xl font-extrabold text-white mt-4 font-jakarta">
                  ₹0<span className="text-xs font-normal text-white/50"> / month</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {[
                    'Up to 15 team members free forever',
                    'Unlimited tasks & Kanban boards',
                    'Core CRM deals & contact pipeline',
                    'Live time tracking timers',
                    'Basic HRMS attendance logging',
                  ].map((feat) => (
                    <li key={feat} className="flex items-center gap-2 text-xs text-white/80">
                      <Check className="h-4 w-4 text-purple-400 shrink-0" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pt-8">
                <Link to="/register">
                  <button className="w-full h-11 rounded-full bg-white/10 hover:bg-white/15 text-white font-bold text-xs font-jakarta transition-colors border border-white/15">
                    Sign Up Free
                  </button>
                </Link>
              </div>
            </div>

            {/* Pro Plan */}
            <div className="p-8 rounded-3xl bg-gradient-to-b from-[#2A0E45] to-[#1C0D2E] text-white border-2 border-purple-500 flex flex-col justify-between shadow-2xl shadow-purple-950/80 relative overflow-hidden">
              <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] font-bold uppercase tracking-wider">
                Recommended
              </div>
              <div>
                <h3 className="text-lg font-bold text-white font-jakarta">Zynx Pro</h3>
                <p className="text-xs text-purple-200/70 mt-1">For growing businesses & agencies</p>
                <div className="text-3xl font-extrabold text-white mt-4 font-jakarta">
                  ₹499<span className="text-xs font-normal text-white/60"> / user / month</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {[
                    'Unlimited team members',
                    'Full CRM + Quotations + HRMS suite',
                    'India GST tax invoice & running bills',
                    'Downloadable PDF invoices & receipts',
                    'Weekly timesheet approval workflows',
                    'Biometric machine attendance sync',
                    'Priority Durrani Tech engineering support',
                  ].map((feat) => (
                    <li key={feat} className="flex items-center gap-2 text-xs text-white/95">
                      <Check className="h-4 w-4 text-purple-300 shrink-0" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pt-8">
                <Link to="/register">
                  <button className="w-full h-11 rounded-full bg-gradient-to-r from-[#9333EA] to-[#C084FC] hover:from-[#8228d4] hover:to-[#b370f5] text-white font-bold text-xs font-jakarta transition-all shadow-lg shadow-purple-900/50">
                    Start 14-Day Free Trial
                  </button>
                </Link>
              </div>
            </div>

            {/* Enterprise Plan */}
            <div className="p-8 rounded-3xl bg-[#150A22] border border-white/10 flex flex-col justify-between shadow-xl">
              <div>
                <h3 className="text-lg font-bold text-white font-jakarta">Enterprise</h3>
                <p className="text-xs text-white/60 mt-1">Dedicated private VPS & on-premise</p>
                <div className="text-3xl font-extrabold text-white mt-4 font-jakarta">Custom</div>
                <ul className="mt-6 space-y-3">
                  {[
                    'Everything included in Pro',
                    'Private VPS / On-Premise Docker stack',
                    'Dedicated database & automated backups',
                    'Custom ERP workflows & integrations',
                    'Dedicated Technical Account Manager',
                    'Guaranteed 99.98% uptime SLA',
                  ].map((feat) => (
                    <li key={feat} className="flex items-center gap-2 text-xs text-white/80">
                      <Check className="h-4 w-4 text-purple-400 shrink-0" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pt-8">
                <a href="https://durranitech.com/contact" target="_blank" rel="noreferrer">
                  <button className="w-full h-11 rounded-full bg-white/10 hover:bg-white/15 text-white font-bold text-xs font-jakarta transition-colors border border-white/15">
                    Contact Enterprise Sales
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FAQ ACCORDION ===================== */}
      <section id="faqs" className="py-20 bg-[#120720] border-t border-purple-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-4xl font-extrabold font-jakarta text-white">
              Frequently Asked Questions
            </h2>
            <p className="text-xs sm:text-sm text-white/60 mt-2">
              Everything you need to know about Zynx Workplace and deployment options.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden transition-colors"
              >
                <button
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between text-sm sm:text-base font-bold font-jakarta text-white"
                >
                  <span>{faq.q}</span>
                  <ChevronDown
                    className={`h-4 w-4 text-purple-300 transition-transform ${faqOpen === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {faqOpen === i && (
                  <div className="px-6 pb-5 text-xs sm:text-sm text-white/70 leading-relaxed border-t border-white/5 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FOOTER ===================== */}
      <footer className="bg-[#0B0413] text-white/60 py-16 text-xs border-t border-purple-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            <div className="col-span-2 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-black text-white tracking-tight font-jakarta">zynx</span>
                <span className="text-[10px] font-bold text-purple-300 bg-purple-900/40 px-2 py-0.5 rounded-full border border-purple-500/20">
                  WORKPLACE
                </span>
              </div>
              <p className="text-white/70 max-w-sm leading-relaxed text-xs">
                The all-in-one business operating system for modern enterprises. Proudly engineered and operated by{' '}
                <a href="https://durranitech.com" target="_blank" rel="noreferrer" className="text-white font-bold hover:underline">
                  Durrani Tech Private Limited
                </a>
                .
              </p>
              <p className="text-white/40 text-[11px] leading-relaxed">
                Corporate HQ: 23A, 2nd Floor Yogesh House, East St, Camp, Pune 411001, Maharashtra, India.
              </p>
            </div>

            <div>
              <p className="font-bold text-white uppercase tracking-wider text-[11px] mb-3">Workplace Modules</p>
              <ul className="space-y-2">
                <li><Link to="/register" className="hover:text-white transition-colors">Agile Projects</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Sales CRM Pipeline</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">HRMS & Attendance</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">India GST Invoicing</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Portfolio Gantt</Link></li>
              </ul>
            </div>

            <div>
              <p className="font-bold text-white uppercase tracking-wider text-[11px] mb-3">Durrani Tech Ecosystem</p>
              <ul className="space-y-2">
                <li><a href="https://durranitech.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">Durrani Tech</a></li>
                <li><a href="https://toolvanta.in" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">Toolvanta</a></li>
                <li><a href="https://apexapps.in" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">ApexApps</a></li>
                <li><a href="https://durranitech.com/contact" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">Engineering Support</a></li>
              </ul>
            </div>

            <div>
              <p className="font-bold text-white uppercase tracking-wider text-[11px] mb-3">Account & Access</p>
              <ul className="space-y-2">
                <li><Link to="/login" className="hover:text-white transition-colors">Employee Sign In</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Create Free Account</Link></li>
                <li><a href="https://durranitech.com/privacy" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="https://durranitech.com/terms" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px]">
            <p>© {new Date().getFullYear()} Durrani Tech Private Limited. All rights reserved.</p>
            <div className="flex items-center gap-4 text-white/50">
              <span>GST Registered</span>
              <span>•</span>
              <span>Biometric Device API Active</span>
              <span>•</span>
              <span>SSL / 256-Bit Encrypted</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
